import React from 'react';
import { ETHERSCAN, PCAP } from './config';
import ForceGraph2D from 'react-force-graph-2d';
import ForceGraph3D from 'react-force-graph-3d';
import SpriteText from 'three-spritetext';
import { SphereGeometry, Mesh, Color, DirectionalLight, MeshPhongMaterial, AmbientLight, Sprite, TextureLoader, SpriteMaterial, Texture, VideoTexture, LinearFilter, Group, MeshBasicMaterial, ShapeGeometry } from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';

// Create the alpha map (only once)
const alphaMap = createCircularAlphaMap(128); // Adjust size as needed
alphaMap.needsUpdate = true;

// Function to create a circular alpha map
function createCircularAlphaMap(size) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  context.fillStyle = 'black';
  context.fillRect(0, 0, size, size);
  context.fillStyle = 'white';
  context.beginPath();
  context.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  context.fill();
  return new Texture(canvas);
}


const normalizeSize = (nodeCount, minNodeCount, maxNodeCount, minSize, maxSize) => {
  return ((maxSize - minSize) * (nodeCount - minNodeCount) / (maxNodeCount - minNodeCount)) + minSize;
};


const isVideo = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('Content-Type');
    return contentType && contentType.startsWith('video/');
  } catch (err) {
    //console.error('Failed to check if file is video', err);
    return false;
  }
};

/**
 * Generates properties for the ForceGraph component based on mode and event handlers.
 * @param {boolean} is3DMode - Determines if the graph is in 3D or 2D mode.
 * @param {Object} graphData - The data structure containing nodes and links for the graph.
 * @param {Object} graphRef - Reference to the graph component.
 * @param {Function} handleBackgroundClick - Handler for background click events.
 * @param {Function} handleNodeDragEnd - Handler for node drag end events.
 * @param {Function} handleNodeDrag - Handler for node drag events.
 * @param {Function} handleEngineStop - Handler for engine stop events.
 * @param {Function} handleZoom - Handler for zoom events.
 * @param {Function} handleClick - Handler for click events on nodes or links.
 * @returns {Object} - Properties to pass to the ForceGraph component.
 */
const getForceGraphProps = (
  isMobile,
  is3DMode,
  graphData,
  graphRef,
  handleBackgroundClick,
  handleNodeDragEnd,
  handleNodeDrag,
  handleEngineStop,
  handleZoom,
  handleClick,
  dataSource,
  filterNFT,
  maxValue,
  imageSize,
  stopCameraSpin,
  setActiveLinkId,
  handleNodeClick,
  showText
) => {
  const commonProps = {
    ref: graphRef,
    graphData: graphData,
    nodeLabel: "label",
    nodeAutoColorBy: "group",
  };

  if (is3DMode) {
    return {
      ...commonProps,
      linkDirectionalParticles: 2,
      linkDirectionalParticleSpeed: 0.008,
      backgroundColor: "#000000",
      onBackgroundClick: handleBackgroundClick,
      onNodeDragEnd: handleNodeDragEnd,
      onNodeDrag: handleNodeDrag,
      onEngineStop: handleEngineStop,
      onZoom: handleZoom,
      onClick: handleClick,

      // Aggressively increase link distance and repulsion
      d3Force: (forceGraphInstance) => {
        forceGraphInstance
          .d3Force('link')
          .distance(2000);  // Set a much higher link distance

        forceGraphInstance
          .d3Force('charge')
          .strength(-500);  // Stronger repulsive force between nodes
      },

      // Speed up the simulation
      //d3AlphaDecay: 0.15, // Increase the decay rate to speed up convergence
      //d3VelocityDecay: 0.4, // Reduce velocity decay to make nodes come to rest faster
      //d3AlphaMin: 0.05,     // Lower the alpha threshold for early stopping

      linkColor: (link) => {
        return 'rgba(255, 255, 0, 1)'; // White color with 90% opacity
      },
      
      onNodeClick: (node, e) => {
        stopCameraSpin();
        navigator.clipboard.writeText(node.id);
        if (dataSource !== PCAP) {
          if (dataSource === ETHERSCAN) {
     
            if (e.shiftKey) {
              let newId = node.id;
              if (newId.includes('.')) {
                newId = newId.split('.')[0];
              }
              const url = `https://etherscan.io/dex/uniswapv2/${newId}`;
              window.open(url, '_blank');
            }
            else if (e.ctrlKey) {
              const explorerBaseUrl = 'https://etherscan.io';
              let middle = '/address/';
              let newId = node.id;
            
              // if it is an NFT we added a period and the token id onto the address
              if (newId.includes('.')) {
                middle = '/nft/'
                newId = newId.replace('.','/');
              }

              const url = explorerBaseUrl + middle + newId;
              window.open(url, '_blank');         
            }
            else {
              handleNodeClick( dataSource, graphData, graphRef, node.id, false);
            }
          }
          else {
            const explorerBaseUrl = 'https://bscscan.com';
            const url = explorerBaseUrl + '/address/' + node.id;
            window.open(url, '_blank');
          }

        } else {
          handleNodeClick(dataSource, graphData, graphRef, node.id, false);
        }
      },

      onLinkClick: (link) => {
        if (dataSource !== PCAP) {
          const explorerBaseUrl = (dataSource === ETHERSCAN) ? 'https://etherscan.io' : 'https://bscscan.com';
          const url = explorerBaseUrl + '/tx/' + link.tx;
          window.open(url, '_blank');
        } else {
          handleNodeClick(dataSource, graphData, graphRef, link.target.id, false);
          setActiveLinkId(link.target.id);
        }
      },

      linkWidth: (link) => {
        const minWidth = 5;
        const maxWidth = 20;
        const size = link.value ? Math.log(link.value + 1) : 1; // +1 to avoid log(0), ensures all values are greater than 0
        const logMaxValue = Math.log(maxValue + 1); // Apply the same transformation to maxValue
        const normalizedValue = (size / logMaxValue) * (maxWidth - minWidth) + minWidth;
        const width = Math.min(Math.max(normalizedValue, minWidth), maxWidth);
        return width;
    },

    nodeThreeObject: (node) => {
      const nodeCount = graphData.nodes.length;
      const multiplier = node.linkCount ? node.linkCount : nodeCount;
      let size = 20;
    
      if (node.icon) {
        if (dataSource === PCAP) {
          size = multiplier * 10;
        } else if (!filterNFT) {
          size = (multiplier + imageSize * 2) / 2;
        } else {
          const minSize = 50;
          const maxSize = 200;
          const minNodeCount = 1;
          const maxNodeCount = Math.max(1000, graphData.nodes.length);
          size = normalizeSize(nodeCount, minNodeCount, maxNodeCount, maxSize, minSize);
        }
      }
    
      size = Math.min(size, 100);
    
      const segments = isMobile ? 16 : 32;
      const sphereGeometry = new SphereGeometry(size, segments, segments);
      const sphereMaterial = new MeshPhongMaterial({
        color: new Color(node.color),
        transparent: true,
        opacity: 0.7,
        shininess: 60,
        specular: new Color(0x111111)
      });
    
      const sphere = new Mesh(sphereGeometry, sphereMaterial);
    
      const loadAsImage = () => {
        const spriteMap = new TextureLoader().load(node.icon);
        const spriteMaterial = new SpriteMaterial({
          map: spriteMap,
          alphaMap,
          transparent: true,
          depthTest: false,
          depthWrite: false
        });
        const sprite = new Sprite(spriteMaterial);
        const spriteScale = size * 2;
        sprite.scale.set(spriteScale, spriteScale, 1);
        const zPosition = showText ? 0 : size + 0.1;
        sprite.position.set(0, 0, zPosition);
        sphere.add(sprite);
        sphereMaterial.opacity = 0.0;
        sphereMaterial.transparent = true;
        return sprite; // So we can remove it later
      };
    
      // Handle base64 SVG
      if (node.icon && node.icon.startsWith('data:image/svg+xml;base64,')) {
        const decodedIcon = atob(node.icon.split(',')[1]);
        const blob = new Blob([decodedIcon], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const spriteMap = new TextureLoader().load(url, () => URL.revokeObjectURL(url));
        const spriteMaterial = new SpriteMaterial({
          map: spriteMap,
          alphaMap,
          transparent: true,
          depthTest: false
        });
        const sprite = new Sprite(spriteMaterial);
        sprite.scale.set(size * 2, size * 2, 1);
        sprite.position.set(0, 0, 0);
        sphere.add(sprite);
        sphereMaterial.opacity = 0.0;
        sphereMaterial.transparent = true;
      }
    
      else if (node.icon) {
          // SVG support: if .svg, load directly via TextureLoader and exit early
          if (node.icon.endsWith('.svg')) {
            const loader = new SVGLoader();
            loader.load(
              node.icon,
              (data) => {
                const paths = data.paths;
                const group = new Group();
          
                paths.forEach((path) => {
                  const material = new MeshBasicMaterial({
                    color: path.color,
                    side: 2, // DoubleSide
                    depthTest: false,
                    depthWrite: false
                  });
          
                  const shapes = SVGLoader.createShapes(path);
                  shapes.forEach((shape) => {
                    const geometry = new ShapeGeometry(shape);
                    const mesh = new Mesh(geometry, material);
                    group.add(mesh);
                  });
                });
          
                group.scale.set(size / 100, size / 100, 1);
                group.position.set(0, 0, showText ? 0 : size + 0.1);
          
                sphere.add(group);
                sphereMaterial.opacity = 0.0;
                sphereMaterial.transparent = true;
              },
              undefined,
              (err) => {
                console.error(`SVG parse error for ${node.id}`, err);
          
                // fallback to PNG
                const pngUrl = node.icon.replace('.svg', '.png');
                new TextureLoader().load(
                  pngUrl,
                  (texture) => {
                    const spriteMaterial = new SpriteMaterial({
                      map: texture,
                      alphaMap,
                      transparent: true,
                      depthTest: false,
                      depthWrite: false
                    });
          
                    const sprite = new Sprite(spriteMaterial);
                    sprite.scale.set(size * 2, size * 2, 1);
                    sprite.position.set(0, 0, showText ? 0 : size + 0.1);
          
                    sphere.add(sprite);
                    sphereMaterial.opacity = 0.0;
                    sphereMaterial.transparent = true;
                  },
                  undefined,
                  (fallbackErr) => {
                    console.error(`PNG fallback also failed for ${node.id}`, fallbackErr);
                  }
                );
              }
            );
          
            return;
          }
          

        const imageSprite = loadAsImage(); // Always load image first
    
        const tryUpgradeToVideo = async () => {
          let isLikelyVideo = false;
          // get the string after the last '/'
          const fileName = node.icon.split('/').pop();
          if (fileName && !fileName.includes('.')) {
            try {
              isLikelyVideo = await isVideo(node.icon);
            } catch (e) {
              console.error(`Error checking MIME type for node ${node.id}`, e);
            }
          }
    
          if (node.icon.endsWith('.mp4') || isLikelyVideo) {
            try {
              const video = document.createElement('video');
              video.crossOrigin = 'anonymous';
              video.loop = true;
              video.muted = true;
              video.playsInline = true;
    
              const source = document.createElement('source');
              source.src = node.icon;
              source.type = 'video/mp4';
              video.appendChild(source);
    
              video.onerror = () => {
                console.error(`Video load error for ${node.id}`, video.error?.message);
              };
    
              video.oncanplay = () => {
                try {
                  const videoTexture = new VideoTexture(video);
                  videoTexture.minFilter = LinearFilter;
                  videoTexture.magFilter = LinearFilter;
    
                  const spriteMaterial = new SpriteMaterial({
                    map: videoTexture,
                    transparent: true,
                    depthTest: false,
                    depthWrite: false
                  });
    
                  const sprite = new Sprite(spriteMaterial);
                  sprite.scale.set(size * 2, size * 2, 1);
                  sprite.position.set(0, 0, showText ? 0 : size + 0.1);
    
                  sphere.remove(imageSprite); // Remove the placeholder
                  sphere.add(sprite);
                  sphereMaterial.opacity = 0.0;
                  sphereMaterial.transparent = true;
    
                  video.play().catch(err => {
                    console.error(`Video play failed for ${node.id}`, err);
                  });
                } catch (err) {
                  console.error(`Video texture error for ${node.id}`, err);
                }
              };
    
              video.load();
            } catch (err) {
              console.error(`Setup video error for ${node.id}`, err);
            }
          }
        };
    
        tryUpgradeToVideo();
      }
    
      else {
        sphereMaterial.opacity = 1.0;
        sphereMaterial.transparent = false;
      }
    
      if (showText && node.label && !node.label.startsWith("0x")) {
        const spriteText = new SpriteText(node.label);
        spriteText.color = "#FFFFFF";
        spriteText.textHeight = 40.0;
        if (filterNFT) size /= 2;
        spriteText.position.set(size / 2, size, size + 0.1);
        sphere.add(spriteText);
      }
    
      return sphere;
    },
    
      threeObject: (graphScene) => {
        const light1 = new DirectionalLight(0xffffff, 1);
        light1.position.set(2, 2, 2).normalize();
        graphScene.add(light1);
      
        const light2 = new DirectionalLight(0xffffff, 1);
        light2.position.set(-2, -2, -2).normalize(); // Light from the opposite direction
        graphScene.add(light2);
      
        const ambientLight = new AmbientLight(0x808080);
        graphScene.add(ambientLight);
      
        return graphScene;
      },
    };
  }

  return {
    ...commonProps,
    nodeCanvasObject: (node, ctx, globalScale) => {
      const baseRadius = 5;
      // Adjust the radius based on the zoom level
      const nodeRadius = Math.max(baseRadius / globalScale, 1); // Ensure the radius doesn't get too small
      const labelPadding = 2; // Space between the node and the text
      // Convert the node.color from a number to a hexadecimal string
      const colorHex = '#' + node.color.toString(16).padStart(6, '0');

      // Draw circle for the node
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
      ctx.fillStyle = colorHex;
      ctx.fill();
  
      // Draw text label only if showText is true
      if (showText) {
          const label = node.id;
          const fontSize = 12/globalScale;
          ctx.font = `${fontSize}px Sans-Serif`;
          ctx.textAlign = 'left'; // Align text to the left of the x-coordinate
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'black'; // Text color
  
          // Position the text to the right of the node
          const textX = node.x + nodeRadius + labelPadding;
          ctx.fillText(label, textX, node.y);
      }
    },
  };
};

/**
 * A wrapper component for rendering the ForceGraph in either 2D or 3D mode.
 * @param {Object} props - Component props including mode, data, and event handlers.
 * @returns {JSX.Element} - The rendered ForceGraph component.
 */
export const GraphComponent = ({
  isMobile,
  is3DMode,
  graphData,
  graphRef,
  handleBackgroundClick,
  handleNodeDragEnd,
  handleNodeDrag,
  handleEngineStop,
  handleZoom,
  handleClick,
  dataSource,
  filterNFT,
  maxValue,
  imageSize,
  stopCameraSpin,
  setActiveLinkId,
  handleNodeClick,
  showText
}) => {
  const ForceGraphComponent = is3DMode ? ForceGraph3D : ForceGraph2D;
  const props = getForceGraphProps(
    isMobile,
    is3DMode,
    graphData,
    graphRef,
    handleBackgroundClick,
    handleNodeDragEnd,
    handleNodeDrag,
    handleEngineStop,
    handleZoom,
    handleClick,
    dataSource,
    filterNFT,
    maxValue,
    imageSize,
    stopCameraSpin,
    setActiveLinkId,
    handleNodeClick,
    showText
  );

  return <ForceGraphComponent {...props} />;
};
